/**
 * React Query hooks for Calendar Event Type operations.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import calendarEventTypeService from '../services/calendar-event-type.service';

interface UseCalendarEventTypeListQueryParams {
  workspaceId?: string;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useCalendarEventTypeListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 50,
  sortBy = 'name',
  order = 'asc',
  search,
  enabled = true,
  refetchInterval = false,
}: UseCalendarEventTypeListQueryParams) => {
  return useQuery({
    queryKey: ['calendar-event-types', workspaceId, page, size, sortBy, order, search],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await calendarEventTypeService.listCalendarEventTypes(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        search,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
    refetchIntervalInBackground: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

interface UseCalendarEventTypeDetailQueryParams {
  eventTypeId: string | undefined;
  workspaceId?: string;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useCalendarEventTypeDetailQuery = ({
  eventTypeId,
  workspaceId,
  idToken,
  enabled = true,
}: UseCalendarEventTypeDetailQueryParams) => {
  return useQuery({
    queryKey: ['calendar-event-type', workspaceId, eventTypeId],
    queryFn: async () => {
      if (!eventTypeId || !workspaceId || !idToken) {
        throw new Error('Event type ID, workspace ID, and token are required');
      }

      return await calendarEventTypeService.getCalendarEventType(workspaceId, eventTypeId, idToken);
    },
    enabled: enabled && !!workspaceId && !!eventTypeId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};
